import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatCurrency } from '@/lib/utils';
import { TEMPLATE_TAGS } from '@/lib/tags';
import type { ContractTemplate, PaginatedResponse } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Percent, DollarSign, Clock, Search, X, Filter, ChevronDown } from 'lucide-react';


export default function MarketplacePage() {
    const [search, setSearch] = useState('');
    const [activeTag, setActiveTag] = useState<string | null>(null);
    const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
    const [tagSearch, setTagSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setTagDropdownOpen(false);
                setTagSearch('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!search) return;
        const timer = setTimeout(() => {
            trackEvent('marketplace_searched', { query: search });
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const filteredTags = TEMPLATE_TAGS.filter((tag) =>
        tag.toLowerCase().includes(tagSearch.toLowerCase())
    );

    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (activeTag) params.set('tag', activeTag);
    const qs = params.toString();

    const { data, isLoading } = useQuery({
        queryKey: ['templates', search, activeTag],
        queryFn: () => api.get<PaginatedResponse<ContractTemplate>>(`/templates${qs ? `?${qs}` : ''}`),
    });

    const templates = data?.data ?? [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-semibold">Marketplace</h1>
                <p className="text-sm text-slate-400">Browse available contract templates</p>
            </div>

            {/* Search & Filters */}
            <div className="space-y-3">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search templates, descriptions, or doms..."
                            className="pl-9"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
                            className={`flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors ${
                                activeTag
                                    ? 'border-violet-600 bg-violet-600/20 text-violet-300'
                                    : 'border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700'
                            }`}
                        >
                            <Filter className="h-4 w-4" />
                            <span className="hidden sm:inline">{activeTag || 'Tag'}</span>
                            <ChevronDown className="h-3 w-3" />
                        </button>
                        {tagDropdownOpen && (
                            <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-slate-700 bg-slate-800 shadow-xl">
                                <div className="p-2">
                                    <Input
                                        value={tagSearch}
                                        onChange={(e) => setTagSearch(e.target.value)}
                                        placeholder="Search tags..."
                                        className="h-8 text-sm"
                                        autoFocus
                                    />
                                </div>
                                <div className="max-h-60 overflow-y-auto px-1 pb-1">
                                    {activeTag && (
                                        <button
                                            onClick={() => {
                                                setActiveTag(null);
                                                setTagDropdownOpen(false);
                                                setTagSearch('');
                                            }}
                                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-400 hover:bg-slate-700"
                                        >
                                            <X className="h-3 w-3" /> Clear filter
                                        </button>
                                    )}
                                    {filteredTags.map((tag) => (
                                        <button
                                            key={tag}
                                            onClick={() => {
                                                setActiveTag(activeTag === tag ? null : tag);
                                                setTagDropdownOpen(false);
                                                setTagSearch('');
                                            }}
                                            className={`flex w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                                                activeTag === tag
                                                    ? 'bg-violet-600/20 text-violet-300'
                                                    : 'text-slate-300 hover:bg-slate-700'
                                            }`}
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                    {filteredTags.length === 0 && (
                                        <p className="px-2 py-1.5 text-sm text-slate-500">No tags found</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {activeTag && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Filtered by:</span>
                        <Badge
                            variant="secondary"
                            className="cursor-pointer gap-1"
                            onClick={() => setActiveTag(null)}
                        >
                            {activeTag}
                            <X className="h-3 w-3" />
                        </Badge>
                    </div>
                )}
            </div>

            {isLoading ? (
                <p className="text-sm text-slate-400">Loading templates...</p>
            ) : templates.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-400">
                        {search || activeTag ? 'No templates match your search.' : 'No templates available yet.'}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {templates.map((template) => (
                        <Link key={template.id} to={`/marketplace/${template.id}`}>
                            <Card className="h-full transition-shadow hover:shadow-lg hover:shadow-slate-900/50">
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <CardTitle className="text-base">{template.title}</CardTitle>
                                        {template.hardcore_mode && (
                                            <Badge variant="destructive">Hardcore</Badge>
                                        )}
                                    </div>
                                    {template.dom && (
                                        <p className="text-xs text-slate-400">
                                            by <span className="text-violet-400">{template.dom.display_name}</span>
                                            {template.dom.average_rating && (
                                                <> — {template.dom.average_rating} stars</>
                                            )}
                                        </p>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    {template.description && (
                                        <p className="mb-3 line-clamp-2 text-sm text-slate-300">
                                            {template.description}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="h-3 w-3" />
                                            {formatCurrency(template.opening_balance)}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Percent className="h-3 w-3" />
                                            {template.interest_rate}% APR
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            {template.compounding_frequency}
                                        </span>
                                    </div>
                                    {template.tags && template.tags.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {template.tags.map((tag) => (
                                                <Badge key={tag} variant="secondary" className="text-[10px]">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
